import { Search } from "@/models/search/Search";

/**
 * When users build a multi-tag search incrementally, each save can create a
 * separate favorite with a growing tag set. Keep only the broadest search per
 * tab + term combination so config and widgets show one row per logical search.
 */
export const dedupeSupersededTagSearches = (searches: Search[]): Search[] => {
  if (searches.length <= 1) {
    return searches;
  }

  const tagCount = (search: Search) =>
    new Set(
      (search.searchTags ?? []).map((tag) => tag.trim()).filter(Boolean)
    ).size;

  const sorted = [...searches].sort((a, b) => {
    const tagDiff = tagCount(b) - tagCount(a);
    if (tagDiff !== 0) {
      return tagDiff;
    }

    const aTime = new Date(a.searchedAt ?? 0).getTime();
    const bTime = new Date(b.searchedAt ?? 0).getTime();
    return bTime - aTime;
  });

  const kept: Search[] = [];

  for (const search of sorted) {
    const tags = new Set(
      (search.searchTags ?? []).map((tag) => tag.trim()).filter(Boolean)
    );
    const term = search.searchTerm?.trim() ?? "";

    const isSubsetOfKept = kept.some((other) => {
      if (other.tab !== search.tab) {
        return false;
      }

      const otherTags = new Set(
        (other.searchTags ?? []).map((tag) => tag.trim()).filter(Boolean)
      );
      const otherTerm = other.searchTerm?.trim() ?? "";

      if (term !== otherTerm) {
        return false;
      }

      return [...tags].every((tag) => otherTags.has(tag));
    });

    if (!isSubsetOfKept) {
      kept.push(search);
    }
  }

  const keptSet = new Set(kept);
  return searches.filter((search) => keptSet.has(search));
};
